import { useProfileStore } from '../../store/useProfileStore';
import { UserCourse, LessonType } from '../../types/userCourse';

import { formatDateWithTimezone } from '../../utils/dateUtils';
import { Course } from '../../types/course';
import { Quiz } from '../../types/quiz';
import { Game } from '../../types/lessonGame';

type CurrentLesson = {
  type: 'video' | 'quiz' | 'game';
  order?: number; // Only for videos
} | null;

interface CourseStatusAndNavProps {
  slug: string;
  userCourse: UserCourse;
  course: Course;
  currentLesson: CurrentLesson;
  onNavigateToLesson: (lesson: CurrentLesson) => void;
  isOpen?: boolean;
  onClose?: () => void;
  quiz?: Quiz | null;
  game?: Game | null;
}

import { IonIcon, IonItem, IonLabel, IonList } from '@ionic/react';
import { ellipseOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { TestPanelLessonPage } from '../testPanel/TestPanelLessonPage';

export const CourseStatusAndNav: React.FC<CourseStatusAndNavProps> = ({ slug, userCourse, course, currentLesson, onNavigateToLesson, isOpen = false, onClose, quiz, game }) => {
  const { activeProfile } = useProfileStore();

  const formattedDate = formatDateWithTimezone(userCourse.lastAccessedAt, activeProfile?.timezone);
  const isNonProd = import.meta.env.VITE_ENV !== 'prod';

  // Helper function to check if a lesson is completed
  const isLessonCompleted = (lessonId: string, lessonType: LessonType): boolean => {
    const lesson = userCourse.userLessons.find(
      l => l.lessonId === lessonId && l.lessonType === lessonType
    );
    // Check for both null and undefined - API might return either
    const isCompleted = lesson ? (lesson.completedAt != null && lesson.completedAt !== '') : false;

    return isCompleted;
  };

  const quizCompleted = isLessonCompleted(course.quizId, LessonType.QUIZ);
  const gameCompleted = isLessonCompleted(course.gameId, LessonType.GAME);

  // Helper to check if current lesson is active
  const isActiveLesson = (type: 'video' | 'quiz' | 'game', order?: number): boolean => {
    if (!currentLesson) return false;
    if (currentLesson.type !== type) return false;
    if (type === 'video' && order !== undefined) {
      return currentLesson.order === order;
    }
    return true;
  };

  return (
    <>
      {/* Overlay for mobile drawer */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - static on lg+, drawer on smaller screens */}
      <aside className={`
        fixed lg:static top-0 left-0 h-full
        w-[300px] min-w-[300px] flex-shrink-0
        bg-white border-r border-gray-200 p-4
        transition-transform duration-300 ease-in-out
        z-50 lg:z-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="space-y-4">
        <h1 className="text-2xl font-bold mb-4">{course.name}</h1>

        {/* Progress Bar */}
        <div className="w-full h-2 bg-medium rounded-full overflow-hidden">
          <div
            className="h-full bg-success rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${Math.min(Math.max(userCourse.progress, 0), 100)}%` }}
          />
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-500">{userCourse.progress}% Complete</p>
          <p className="text-xs text-gray-500">Last Activity on {formattedDate}</p>
        </div>

        <hr className="border-gray-200 my-4" />

        <IonList lines="full">
          {course.videos && course.videos.length > 0 ? (
            course.videos
              .sort((a, b) => a.order - b.order)
              .map((video, index) => {
                // Each video is tracked as a separate lesson with lessonId format: UUID with order number at the end
                // e.g., order 1 -> "00000000-0000-0000-0000-000000000001"
                // e.g., order 12 -> "00000000-0000-0000-0000-000000000012"
                const videoLessonId = `00000000-0000-0000-0000-${video.order.toString().padStart(12, '0')}`;
                const videoCompleted = isLessonCompleted(videoLessonId, LessonType.VIDEO);
                const isActive = isActiveLesson('video', video.order);
                return (
                  <IonItem
                    key={video.order}
                    className="w-full cursor-pointer"
                    lines="none"
                    detail={false}
                    button
                    onClick={() => {
                      onNavigateToLesson({ type: 'video', order: video.order });
                      onClose?.();
                    }}
                    style={{
                      '--background': isActive ? '#FCEEF0' : 'transparent',
                      '--color': isActive ? 'var(--ion-color-primary)' : 'inherit',
                    }}
                  >
                    <IonLabel className="ion-text-wrap" color={isActive ? "primary" : undefined}>{video.title}</IonLabel>
                    <IonIcon
                      slot="end"
                      icon={videoCompleted ? checkmarkCircleOutline : ellipseOutline}
                      color={videoCompleted ? "success" : "medium"}
                    />
                  </IonItem>
                );
              })
          ) : (
            <p className="text-sm text-gray-500 px-4">No videos available</p>
          )}

          {course.quizTitle && course.quizId && (
            <IonItem
              className="w-full cursor-pointer"
              lines="none"
              detail={false}
              button
              onClick={() => {
                onNavigateToLesson({ type: 'quiz' });
                onClose?.();
              }}
              style={{
                '--background': isActiveLesson('quiz') ? '#FCEEF0' : 'transparent',
                '--color': isActiveLesson('quiz') ? 'var(--ion-color-primary)' : 'inherit',
              }}
            >
              <IonLabel color={isActiveLesson('quiz') ? "primary" : undefined}>{course.quizTitle}</IonLabel>
              <IonIcon
                slot="end"
                icon={quizCompleted ? checkmarkCircleOutline : ellipseOutline}
                color={quizCompleted ? "success" : "medium"}
              />
            </IonItem>
          )}

          {course.gameTitle && course.gameId && (
            <IonItem
              className="w-full cursor-pointer"
              lines="none"
              detail={false}
              button
              onClick={() => {
                onNavigateToLesson({ type: 'game' });
                onClose?.();
              }}
              style={{
                '--background': isActiveLesson('game') ? '#FCEEF0' : 'transparent',
                '--color': isActiveLesson('game') ? 'var(--ion-color-primary)' : 'inherit',
              }}
            >
              <IonLabel color={isActiveLesson('game') ? "primary" : undefined}>{course.gameTitle}</IonLabel>
              <IonIcon
                slot="end"
                icon={gameCompleted ? checkmarkCircleOutline : ellipseOutline}
                color={gameCompleted ? "success" : "medium"}
              />
            </IonItem>
          )}
        </IonList>

        {/* Test Panel - positioned at bottom */}
        {isNonProd && (
          <div className="mt-auto pt-4">
            <TestPanelLessonPage
              lessonType={currentLesson?.type || null}
              quiz={quiz}
              game={game}
            />
          </div>
        )}
      </div>
    </aside>
    </>
  );
};
